import { type ReactNode, useEffect, useState } from 'react';

// Small pieces that every page was otherwise re-inventing with a className and
// a <span>, plus the two behaviours that were quietly missing: copying that
// says whether it worked, and a destructive button that asks first.

// A status word carries more than its text — DONE and FAILED belong to
// different colours, and a table of grey words makes the reader parse each one.
export function Pill({ status }: { status: string }) {
  const tone =
    status === 'DONE' || status === 'DELIVERED' || status === 'ACTIVE'
      ? 'ok'
      : status === 'FAILED' || status === 'REJECTED' || status === 'SUSPENDED' || status === 'revoked'
        ? 'bad'
        : status === 'PENDING' || status === 'QUEUED' || status === 'PROCESSING'
          ? 'busy'
          : 'neutral';
  return <span className={`pill pill-${tone}`}>{status.toLowerCase()}</span>;
}

// Copy that reports. navigator.clipboard is unavailable on an insecure origin
// and rejects inside a denied permission prompt; the old code awaited it with
// no catch, so a failed copy looked exactly like a successful one and the
// developer pasted whatever was in the buffer before.
export function CopyButton({
  text,
  label = 'Copy',
  className = 'secondary',
}: {
  text: string;
  label?: string;
  className?: string;
}) {
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => setState('idle'), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  return (
    <button
      type="button"
      className={className}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setState('done');
        } catch {
          setState('failed');
        }
      }}
    >
      {state === 'done' ? 'Copied' : state === 'failed' ? 'Select it by hand' : label}
    </button>
  );
}

// A button whose action cannot be undone turns into a question in place.
//
// Revoking a key used to happen on the first click: one mis-click on the wrong
// row and a production integration is down until somebody notices. In place
// rather than a modal, because the row it belongs to is the context.
export function DangerAction({
  label,
  confirmLabel,
  question,
  onConfirm,
  disabled,
}: {
  label: string;
  confirmLabel: string;
  question: string;
  onConfirm: () => void;
  disabled?: boolean;
}) {
  const [asking, setAsking] = useState(false);

  if (!asking) {
    return (
      <button className="link" disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </button>
    );
  }
  return (
    <span className="confirm-inline">
      <span className="small muted">{question}</span>
      <button
        className="danger-btn small"
        disabled={disabled}
        onClick={() => {
          setAsking(false);
          onConfirm();
        }}
      >
        {confirmLabel}
      </button>
      <button className="secondary small" disabled={disabled} onClick={() => setAsking(false)}>
        Keep it
      </button>
    </span>
  );
}

// What a page shows before it has anything to show. An empty table with a grey
// "Nothing yet" tells a new developer nothing about what to do next.
export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      {children ? <div className="muted small">{children}</div> : null}
    </div>
  );
}

// Page heading with the one line that says what the page is for.
export function PageHead({ title, lead }: { title: string; lead?: string }) {
  return (
    <header className="page-head">
      <h2>{title}</h2>
      {lead ? <p className="muted">{lead}</p> : null}
    </header>
  );
}

// A table that is wider than its panel scrolls inside the panel. Applying the
// scroll to the table itself (display: block) also made it shrink to its
// content, so every table on every page sat at 60% width with a ragged right
// edge — which is what it looked like until 2026-08-18.
export function TableWrap({ children }: { children: ReactNode }) {
  return <div className="table-wrap">{children}</div>;
}
